import React, { useMemo, useState } from 'react';
import { Btn, Loading, Msg, Select, useAdminGet } from '../kit';
import { Modal } from '../email/emailKit';
import {
  Collapse, MODE_LABEL, NoteLine, Pill, type RecrawlCtx, RUN_STATUS, S, type UniRow, arr, fmtAgo, fmtN, fmtUsd, n0, num, obj,
  queueText, scrapeResults, str, useAction,
} from './shared';

/**
 * Starting runs by hand.
 *
 *  - RunStartModal: one university (picked here, or given), one of Probe / Dry run / Full run,
 *    with what each does in plain words, the estimated enrichment cost and — when it cannot start —
 *    why (an open run, switched off, a manual-file university without its file). POST probe {code}
 *    or run_now {code, mode}.
 *  - RunAllModal: "Queue all universities" — GET run_all_plan {mode, include_paused} lists what would
 *    queue and what is skipped and why; POST run_all {mode, codes, confirm} queues exactly that list.
 *    The engine starts the runs one at a time.
 *  - RunButton: the small "Run…" action on a university card / row.
 */

export type RunMode = 'probe_only' | 'dry_run' | 'full';
export const RUN_MODES: RunMode[] = ['probe_only', 'dry_run', 'full'];

export const RUN_MODE_INFO: Record<RunMode, { label: string; short: string; what: string[] }> = {
  probe_only: {
    label: 'Probe', short: 'Is the website reachable?',
    what: ['Checks that the university’s portal and a few of its technology pages answer.',
      'Nothing is scraped, nothing costs anything and nothing changes.'],
  },
  dry_run: {
    label: 'Dry run', short: 'Scrape and compare — nothing changes',
    what: ['Scrapes the university’s website and compares it with the master: what is new, changed, moved or gone.',
      'New and changed records are enriched while the budget allows (this costs; over the budget they are skipped).',
      'Nothing is written to the website or the master. The results show what a full run would change.'],
  },
  full: {
    label: 'Full run', short: 'Scrape, compare, enrich — then approval',
    what: ['Scrapes the website, compares it with the master and enriches the new and changed records (this costs).',
      'It then stops at “awaiting approval”: an admin reviews the changes and approves or rejects them.',
      'Approving takes a backup first, then updates the website and the master.'],
  },
};

/** A manual-file university has no website: its runs read the uploaded source file instead. */
const MANUAL_WHAT: Partial<Record<RunMode, string[]>> = {
  dry_run: ['Reads the uploaded source file and compares it with the master: what is new, changed or gone.',
    'New and changed records are enriched while the budget allows (this costs; over the budget they are skipped).',
    'Nothing is written to the website or the master. The results show what a full run would change.'],
  full: ['Reads the uploaded source file, compares it with the master and enriches the new and changed records (this costs).',
    'It then stops at “awaiting approval”: an admin reviews the changes and approves or rejects them.',
    'Approving takes a backup first, then updates the website and the master.'],
};

const blockOf = (u: UniRow | undefined, mode: RunMode): string => {
  if (!u) return 'Pick a university.';
  const b = obj(u.run_blocked);
  if (mode in b) return str(b[mode]);
  // An older API without run_blocked: the open run is the one rule the panel can see.
  return num(u.open_run_id) !== null ? `Run #${u.open_run_id} is still open.` : '';
};

/** The first mode that can start (the given one when it can). */
const firstOpen = (u: UniRow | undefined, want?: RunMode): RunMode => {
  if (want && !blockOf(u, want)) return want;
  return (['dry_run', 'full', 'probe_only'] as RunMode[]).find(m => !blockOf(u, m)) || want || 'dry_run';
};

const CostLine: React.FC<{ u: UniRow; mode: RunMode; settings: Record<string, any> }> = ({ u, mode, settings }) => {
  if (mode === 'probe_only') return null;
  const est = obj(u.enrich_estimate);
  const cap = num(settings.max_enrich_usd_per_run) ?? num(est.per_run_cap_usd);
  if (num(est.records) === null) {
    const why = est.applied
      ? `the latest results${num(est.from_run_id) !== null ? ` (run #${est.from_run_id})` : ''} are already on the website, so only what changed since is enriched`
      : 'no earlier results for this university';
    return <div className="text-xs text-grayscale-1200">Enrichment cost: known once the comparison is done ({why}){cap !== null ? `; at most ${fmtUsd(cap)} per run` : ''}.</div>;
  }
  return (
    <div className="text-xs text-grayscale-1200">
      Enrichment: up to about <b>{fmtUsd(est.usd)}</b> ({fmtN(est.records)} new or changed records in the latest results
      {num(est.from_run_id) !== null ? `, run #${est.from_run_id}` : ''}; records already enriched cost nothing).
      {cap !== null && n0(est.usd) > cap && <span className="text-amber-800"> That is over the {fmtUsd(cap)} per-run cap: {mode === 'full' ? 'the run will be held until an admin approves the cost' : 'the dry run will skip enrichment'}.</span>}
    </div>
  );
};

export const RunStartModal: React.FC<{
  ctx: RecrawlCtx; code?: string; mode?: RunMode; onClose: () => void; onUpload?: () => void;
}> = ({ ctx, code, mode, onClose, onUpload }) => {
  const [pick, setPick] = useState(str(code).toUpperCase());
  const u = ctx.uniByCode.get(pick);
  const [chosen, setChosen] = useState<RunMode>(() => firstOpen(ctx.uniByCode.get(str(code).toUpperCase()), mode));
  const st = useAdminGet<any>(S, 'settings', {}, []);
  const settings = obj(obj(st.data).settings);
  const act = useAction(() => ctx.reloadAll());
  const [started, setStarted] = useState<any>(null);

  const why = blockOf(u, chosen);
  const manualNoFile = !!u && u.kind === 'manual_file' && u.has_upload === false;
  const info = RUN_MODE_INFO[chosen];
  const what = (u?.kind === 'manual_file' && MANUAL_WHAT[chosen]) || info.what;
  const scrape = u?.last_scrape;

  const start = async () => {
    if (!u || why) return;
    const body = chosen === 'probe_only' ? { code: u.code } : { code: u.code, mode: chosen };
    const r = await act.post('start', chosen === 'probe_only' ? 'probe' : 'run_now', body,
      x => `${info.label} of ${u.code} queued as run #${str(obj(x.run).id)}.`);
    if (r) setStarted(obj(r.run));
  };

  const codeOpts = ['', ...ctx.universities.map(x => x.code)];
  const codeLabels: Record<string, string> = { '': 'Pick a university…' };
  ctx.universities.forEach(x => { codeLabels[x.code] = `${x.code} — ${x.name}`; });

  if (started) {
    const q = queueText(started);
    return (
      <Modal title={`${info.label} queued`} onClose={onClose}
        footer={<><Btn onClick={onClose}>Close</Btn><Btn kind="primary" onClick={() => { onClose(); ctx.openRun(str(started.id)); }}>Open run #{str(started.id)}</Btn></>}>
        <p><b>{u?.name || pick}</b>: run #{str(started.id)} is <b>{RUN_STATUS[str(started.status)]?.label || str(started.status)}</b>{q ? ` — ${q}` : ''}.</p>
        <p className="text-xs text-grayscale-1100">One run goes at a time across all universities; the next one in the queue starts when it finishes.
          {chosen === 'full' && ' When it reaches “awaiting approval” it appears under Waiting for you on the Overview.'}</p>
      </Modal>
    );
  }

  return (
    <Modal title={u ? `Start a run: ${u.name}` : 'Start a run'} onClose={onClose}
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={!u || !!why || !!act.busy} onClick={start}>
          {act.busy ? 'Queuing…' : `Start ${info.label.toLowerCase()}`}
        </Btn>
      </>}>
      {!code && (
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0 [&_select]:max-w-full">University
          <Select value={pick} onChange={v => { setPick(v); setChosen(firstOpen(ctx.uniByCode.get(v), chosen)); }} options={codeOpts} labels={codeLabels} />
        </label>
      )}
      {u && (
        <div className="text-xs text-grayscale-1200">
          {scrape ? <>Latest {MODE_LABEL[str(scrape.mode)] || str(scrape.mode)} #{str(scrape.id)}: {RUN_STATUS[str(scrape.status)]?.label || str(scrape.status)}
            {scrapeResults(scrape) ? ` — ${scrapeResults(scrape)}` : ''} · {fmtAgo(scrape.finished_at || scrape.started_at || scrape.created_at)}</>
            : 'Never crawled by the engine yet.'}
        </div>
      )}
      <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Kind of run">
        {RUN_MODES.map(m => {
          const b = blockOf(u, m);
          const on = chosen === m;
          return (
            <label key={m} className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${b ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${on ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300'}`}>
              <input type="radio" name="run-mode" className="mt-1" checked={on} disabled={!!b && !!u} onChange={() => setChosen(m)} />
              <span className="flex flex-col">
                <span className="text-sm"><b>{RUN_MODE_INFO[m].label}</b> <span className="text-grayscale-1100">— {RUN_MODE_INFO[m].short}</span></span>
                {b && u && <span className="text-xs text-amber-800">{b}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {u && (
        <div className="rounded-lg bg-grayscale-200/60 px-3 py-2 flex flex-col gap-1">
          <div className="text-xs font-bold text-grayscale-1300">What happens</div>
          <ul className="list-disc pl-5 text-xs text-grayscale-1200 flex flex-col gap-0.5">
            {what.map(w => <li key={w}>{w}</li>)}
            {chosen === 'full' && settings.auto_approve === true && (
              <li className="text-amber-800">Auto-approve is on: when no approval rule trips, the changes go live without waiting for an admin.</li>
            )}
            <li>One run at a time across all universities: it may wait in the queue.</li>
          </ul>
          <CostLine u={u} mode={chosen} settings={settings} />
        </div>
      )}
      {manualNoFile && (
        <div className="flex items-center gap-2 flex-wrap text-xs rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
          This university’s technologies come from a file, and none is on the server yet.
          <Btn small kind="primary" onClick={() => { onClose(); if (onUpload) onUpload(); else ctx.openUniversity(u.code); }}>Upload the source file</Btn>
        </div>
      )}
      {why && u && !manualNoFile && <div className="text-xs text-amber-800">{RUN_MODE_INFO[chosen].label} cannot start: {why}</div>}
      <NoteLine note={act.note} />
    </Modal>
  );
};

const PLAN_MODES: RunMode[] = ['dry_run', 'full', 'probe_only'];

export const RunAllModal: React.FC<{ ctx: RecrawlCtx; onClose: () => void }> = ({ ctx, onClose }) => {
  const [mode, setMode] = useState<RunMode>('dry_run');
  const [withPaused, setWithPaused] = useState(false);
  const plan = useAdminGet<any>(S, 'run_all_plan', { mode, include_paused: withPaused ? 'true' : '' }, [mode, withPaused, ctx.tick]);
  const p = obj(plan.data);
  const eligible = arr(p.eligible);
  const skipped = arr(p.skipped);
  const spend = obj(p.month_spend);
  const act = useAction(() => ctx.reloadAll());
  const [result, setResult] = useState<any>(null);
  const info = RUN_MODE_INFO[mode];
  const ahead = n0(p.queued_now) + n0(p.running_now);
  const byReason = useMemo(() => {
    const m = new Map<string, any[]>();
    skipped.forEach(s => { const k = str(s.reason); m.set(k, [...(m.get(k) || []), s]); });
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [skipped]);

  const go = async () => {
    const r = await act.post('all', 'run_all', { mode, include_paused: withPaused, codes: eligible.map(e => str(e.code)), confirm: true },
      x => `${arr(x.queued).length} run${arr(x.queued).length === 1 ? '' : 's'} queued${arr(x.skipped).length ? `, ${arr(x.skipped).length} skipped` : ''}.`);
    if (r) setResult(r);
  };

  if (result) {
    const queued = arr(result.queued);
    const sk = arr(result.skipped);
    return (
      <Modal title={`${info.label}: ${queued.length} queued`} onClose={onClose}
        footer={<><Btn onClick={onClose}>Close</Btn><Btn kind="primary" onClick={() => { onClose(); ctx.goRuns({ status: 'queued,running,promoting,post_load' }); }}>See them in Runs</Btn></>}>
        <p>The runs start one at a time, in this order. Queued runs can be cancelled from their run drawer.</p>
        <div className="max-h-56 overflow-auto rounded-lg border border-grayscale-300 text-xs">
          {queued.map(x => (
            <div key={str(x.run_id)} className="flex items-center gap-2 px-2 py-1 border-t border-grayscale-200 first:border-t-0">
              <b className="w-14">{str(x.code)}</b><span className="flex-1 truncate text-grayscale-1200">{str(x.name)}</span>
              <span className="text-grayscale-1100">run #{str(x.run_id)}</span>
              <Pill tone={x.status === 'running' ? 'blue' : 'navy'}>{x.status === 'running' ? 'running' : queueText({ status: 'queued', queue_position: x.queue_position }) || 'queued'}</Pill>
            </div>
          ))}
          {!queued.length && <div className="px-2 py-2 text-grayscale-1100">Nothing was queued.</div>}
        </div>
        {sk.length > 0 && (
          <Collapse title={`Skipped (${sk.length})`}>
            <ul className="text-xs text-grayscale-1200 flex flex-col gap-0.5">{sk.map(s => <li key={str(s.code)}><b>{str(s.code)}</b> — {str(s.reason)}</li>)}</ul>
          </Collapse>
        )}
      </Modal>
    );
  }

  return (
    <Modal title="Queue all universities" onClose={onClose}
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={!eligible.length || plan.loading || !!act.busy} onClick={go}>
          {act.busy ? 'Queuing…' : `Queue ${eligible.length} ${info.label.toLowerCase()}${eligible.length === 1 ? '' : 's'}`}
        </Btn>
      </>}>
      <div className="flex items-center gap-2 flex-wrap min-w-0 [&_select]:max-w-full">
        <Select value={mode} onChange={v => setMode(v as RunMode)} options={PLAN_MODES}
          labels={{ dry_run: 'Dry run — scrape and compare, nothing changes', full: 'Full run — then approval', probe_only: 'Probe — reachability only' }} />
        <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200">
          <input type="checkbox" checked={withPaused} onChange={e => setWithPaused(e.target.checked)} /> include paused and flagged universities
        </label>
      </div>
      <ul className="list-disc pl-5 text-xs text-grayscale-1200 flex flex-col gap-0.5">
        {info.what.map(w => <li key={w}>{w}</li>)}
      </ul>
      {plan.error && !plan.data ? <Msg kind="error">{plan.error}</Msg> : plan.loading && !plan.data ? <Loading label="Checking every university…" /> : (
        <>
          <div className="text-sm">
            <b>{eligible.length}</b> universit{eligible.length === 1 ? 'y' : 'ies'} will be queued, one run each; <b>{skipped.length}</b> skipped.
            {' '}They run one at a time{ahead ? ` after the ${ahead} run${ahead === 1 ? '' : 's'} already going or queued` : ''}.
          </div>
          {mode !== 'probe_only' && (
            <div className="text-xs text-grayscale-1200">
              Enrichment: up to about <b>{fmtUsd(p.est_usd)}</b> from the latest results
              {n0(p.est_unknown) > 0 ? ` (+ ${p.est_unknown} universit${n0(p.est_unknown) === 1 ? 'y' : 'ies'} with no estimate yet: no earlier results, or their latest results are already on the website)` : ''};
              {' '}this month {fmtUsd(spend.spent_usd)} of {fmtUsd(spend.cap_usd)} is spent. A run that would go over the budget
              {mode === 'full' ? ' is held for an admin.' : ' skips enrichment.'}
              {mode === 'full' && (p.auto_approve ? ' Auto-approve is on: runs where no approval rule trips go live without an admin.' : ' Each full run stops at “awaiting approval”.')}
            </div>
          )}
          {eligible.length > 0 && (
            <Collapse title={`Will be queued (${eligible.length})`}>
              <div className="flex flex-wrap gap-1">
                {eligible.map(e => (
                  <Pill key={str(e.code)} tone="navy" title={`${str(e.name)}${num(obj(e.estimate).records) !== null ? ` — about ${fmtUsd(obj(e.estimate).usd)}` : ''}`}>{str(e.code)}</Pill>
                ))}
              </div>
            </Collapse>
          )}
          {skipped.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1300">Skipped</div>
              <ul className="text-xs text-grayscale-1200 flex flex-col gap-0.5 max-h-40 overflow-auto">
                {byReason.map(([reason, list]) => (
                  <li key={reason}><b>{list.map(s => str(s.code)).join(', ')}</b> — {reason}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <NoteLine note={act.note} />
    </Modal>
  );
};

/** The small run action on a university card or row. Stops the click there (the card / row opens the drawer). */
export const RunButton: React.FC<{ u: UniRow; onRun: (code: string) => void }> = ({ u, onRun }) => {
  // A manual-file university without its file still opens the dialog: it offers the upload.
  const needsFile = u.kind === 'manual_file' && u.has_upload === false && u.enabled && num(u.open_run_id) === null;
  const blocked = !needsFile && RUN_MODES.every(m => !!blockOf(u, m));
  const reason = blocked ? blockOf(u, 'dry_run') || blockOf(u, 'full') : '';
  return (
    <span onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} className="inline-flex">
      <button type="button" disabled={blocked} title={blocked ? reason : `Start a probe, dry run or full run of ${u.code}`}
        onClick={() => onRun(u.code)}
        className="text-[11px] leading-none rounded-full border border-primary-300 px-2 py-1 text-primary-700 hover:bg-primary-100 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
        Run ▸
      </button>
    </span>
  );
};
